/**
 * Where each state publishes its own sales-tax rates (N24).
 *
 * **FixyDany does not know anybody's tax rate and must not appear to.** Rates
 * vary by state, county and city, they change on schedules nobody here tracks,
 * and a platform that pre-fills one has told a tradesperson what to charge the
 * government. So this file holds no rates and never will: it holds the address
 * of the office that does, and the FixyBee types what they know.
 *
 * **Why a table and not a search link.** "Sales tax rate + state name" in a
 * search engine returns aggregator sites that sell lead generation and are
 * wrong at county boundaries. The Department of Revenue is the only source
 * that is right by definition.
 *
 * **The five states with no sales tax are here too**, pointing at the same
 * offices. Alaska has local sales taxes despite having no state one, and a
 * FixyBee in Delaware still has a gross receipts tax to ask about — sending
 * them to a page that says "no sales tax" is a better answer than sending them
 * nowhere, and a missing row would read as "we forgot your state".
 *
 * **The fallback rule, and it is a real rule and not a shrug:** a state this
 * map does not name — which today is none of the 50, plus DC and the
 * territories — falls back to `FALLBACK`, the Federation of Tax
 * Administrators' own directory of every state agency. It is one click further
 * from the answer and it is never wrong, which is the right trade for a link
 * nobody is maintaining. `taxAuthorityFor` never returns null, so no caller
 * has to decide what to do about a missing link, and no screen can render a
 * dead one.
 */

/** The Federation of Tax Administrators' directory of state agencies. */
export const FALLBACK = "https://taxadmin.org/state-tax-agencies/";

export const TAX_AUTHORITIES = {
  AL: "https://www.revenue.alabama.gov/sales-use/",
  AK: "https://tax.alaska.gov/",
  AZ: "https://azdor.gov/transaction-privilege-tax",
  AR: "https://www.dfa.arkansas.gov/excise-tax/sales-and-use-tax/",
  CA: "https://www.cdtfa.ca.gov/taxes-and-fees/sales-use-tax-rates.htm",
  CO: "https://tax.colorado.gov/sales-tax",
  CT: "https://portal.ct.gov/drs/sales-tax/sales-tax-information",
  DE: "https://revenue.delaware.gov/",
  FL: "https://floridarevenue.com/taxes/taxesfees/Pages/sales_tax.aspx",
  GA: "https://dor.georgia.gov/taxes/business-taxes/sales-use-tax",
  HI: "https://tax.hawaii.gov/geninfo/get/",
  ID: "https://tax.idaho.gov/taxes/sales-use/",
  IL: "https://tax.illinois.gov/research/taxrates/sales.html",
  IN: "https://www.in.gov/dor/business-tax/sales-tax/",
  IA: "https://revenue.iowa.gov/taxes/tax-guidance/sales-use-tax",
  KS: "https://www.ksrevenue.gov/salesrate.html",
  KY: "https://revenue.ky.gov/Business/Sales-Use-Tax/Pages/default.aspx",
  LA: "https://revenue.louisiana.gov/SalesTax",
  ME: "https://www.maine.gov/revenue/taxes/sales-use-service-provider-tax",
  MD: "https://www.marylandtaxes.gov/business/sales-use/index.php",
  MA: "https://www.mass.gov/guides/sales-and-use-tax",
  MI: "https://www.michigan.gov/taxes/business-taxes/sales-use-tax",
  MN: "https://www.revenue.state.mn.us/sales-and-use-tax",
  MS: "https://www.dor.ms.gov/business/sales-tax",
  MO: "https://dor.mo.gov/taxation/business/tax-types/sales-use/",
  MT: "https://mtrevenue.gov/taxes/",
  NE: "https://revenue.nebraska.gov/businesses/nebraska-sales-and-use-tax",
  NV: "https://tax.nv.gov/taxes/sales-use-tax/",
  NH: "https://www.revenue.nh.gov/taxes-glance",
  NJ: "https://www.nj.gov/treasury/taxation/su.shtml",
  NM: "https://www.tax.newmexico.gov/businesses/gross-receipts-overview/",
  NY: "https://www.tax.ny.gov/bus/st/stidx.htm",
  NC: "https://www.ncdor.gov/taxes-forms/sales-and-use-tax",
  ND: "https://www.tax.nd.gov/business/sales-and-use-tax",
  OH: "https://tax.ohio.gov/business/ohio-business-taxes/sales-and-use",
  OK: "https://oklahoma.gov/tax/businesses/sales-use-tax.html",
  OR: "https://www.oregon.gov/dor/Pages/index.aspx",
  PA: "https://www.pa.gov/agencies/revenue/resources/tax-types-and-information/sales-use-and-hotel-occupancy-tax.html",
  RI: "https://tax.ri.gov/tax-sections/sales-excise/sales-use-tax",
  SC: "https://dor.sc.gov/tax/sales",
  SD: "https://dor.sd.gov/businesses/taxes/sales-use-tax/",
  TN: "https://www.tn.gov/revenue/taxes/sales-and-use-tax.html",
  TX: "https://comptroller.texas.gov/taxes/sales/",
  UT: "https://tax.utah.gov/sales",
  VT: "https://tax.vermont.gov/business/sales-and-use",
  VA: "https://www.tax.virginia.gov/retail-sales-and-use-tax",
  WA: "https://dor.wa.gov/taxes-rates/sales-and-use-tax-rates",
  WV: "https://tax.wv.gov/Business/SalesAndUseTax/Pages/SalesAndUseTax.aspx",
  WI: "https://www.revenue.wi.gov/Pages/Businesses/Sales-Use.aspx",
  WY: "https://revenue.wyo.gov/divisions/excise-tax-division",
};

/**
 * The page this FixyBee should read, always. Never null: a caller that has to
 * decide what to do about a missing link is a screen that can render a dead one.
 */
export function taxAuthorityFor(state) {
  const key = String(state || "").trim().toUpperCase().slice(0, 2);
  return TAX_AUTHORITIES[key] || FALLBACK;
}

export default taxAuthorityFor;
